import { get, isNil } from 'lodash';
import agGridUtils from '../../../utils/ag-grid-utils';
import numberFormats from '@enums/number-formats';
import { displayCompetitorHistoricalPrices } from '@enums/feature-flags';

/**
 * Recursively adds headerTooltip to each column, falling back to headerName.
 * @param {Array} columns
 * @returns {Array}
 */
function addHeaderTooltips(columns) {
  return columns.map(col => {
    if (col.children) {
      return { ...col, children: addHeaderTooltips(col.children) };
    }
    if (col.headerName) {
      return { ...col, headerTooltip: col.headerTooltip || col.headerName };
    }
    return col;
  });
}

/**
 * Builds the column definitions for the competitor price ignore AG Grid.
 * Accepts the Vue component instance directly so all translations and methods
 * are called with the correct context without manual binding.
 *
 * @param {Vue} vm - The CompetitorPriceIgnore Vue component instance
 * @returns {Array} AG Grid column definitions
 */
export default function buildColumnDefs(vm) {
  const {
    feedCompetitors,
    storeGroupCompetitorMatch,
    storegroupKeyNameMap,
    storeGroupChains,
    toggleLogic = {},
  } = vm;
  const showHistoricalCompetitorPrices = !!toggleLogic[displayCompetitorHistoricalPrices];

  const tkey = key => vm.$tkey(key);
  const t = key => vm.$t(key);

  const currencyFmt = params => {
    if (isNil(params.value)) return '-';
    return vm.formatNumber({
      number: Number(params.value),
      format: numberFormats.priceFormat,
      nullAsDash: true,
    });
  };

  const deltaFmt = params => {
    if (isNil(params.value)) return '-';
    return vm.formatNumber({
      number: Number(params.value),
      format: numberFormats.priceFormat,
      zeroAsDash: true,
    });
  };

  const productColumns = [
    {
      headerName: tkey('columns.productKey'),
      field: 'productKeyDisplay',
      colId: 'productKeyDisplay',
      pinned: 'left',
      width: 100,
      cellDataType: 'text',
    },
    {
      headerName: tkey('columns.clientProductKey'),
      field: 'clientProductKey',
      colId: 'clientProductKey',
      pinned: 'left',
      width: 100,
      cellDataType: 'text',
    },
    {
      headerName: tkey('columns.articleDescription'),
      field: 'productName',
      colId: 'productName',
      pinned: 'left',
      width: 200,
      cellDataType: 'text',
      headerClass: 'border-right',
      cellClass: 'border-right',
    },
  ];

  const buildTsgColumns = (tsgKey, tsgDescription) => {
    const allowedCompetitorNames = storeGroupCompetitorMatch[tsgDescription];
    const tsgFeedCompetitors = allowedCompetitorNames
      ? feedCompetitors.filter(comp =>
          allowedCompetitorNames.includes(
            comp.competitorDisplayDescription || comp.competitorDescription
          )
        )
      : feedCompetitors;

    const ownPriceColumns = [
      {
        headerName: `${tkey('columns.referenceDatePrice')} (${t('clientCurrencySymbol')})`,
        field: `toolStoreGroups.${tsgKey}.referenceDatePrice`,
        colId: `tsg::${tsgKey}::referenceDatePrice`,
        width: 110,
        valueFormatter: currencyFmt,
        filter: 'agNumberColumnFilter',
        filterParams: agGridUtils.getNumericFilterParams(),
      },
      {
        headerName: `${tkey('columns.livePrice')} (${t('clientCurrencySymbol')})`,
        field: `toolStoreGroups.${tsgKey}.livePrice`,
        colId: `tsg::${tsgKey}::livePrice`,
        width: 110,
        valueFormatter: currencyFmt,
        filter: 'agNumberColumnFilter',
        filterParams: agGridUtils.getNumericFilterParams(),
      },
      {
        headerName: `${tkey('columns.priceDelta')} (${t('clientCurrencySymbol')})`,
        colId: `tsg::${tsgKey}::priceDelta`,
        width: 110,
        headerClass: 'border-right',
        cellClass: 'border-right',
        valueGetter: params => {
          const live = get(params, `data.toolStoreGroups.${tsgKey}.livePrice`);
          const ref = get(params, `data.toolStoreGroups.${tsgKey}.referenceDatePrice`);
          if (isNil(live) || isNil(ref)) return null;
          return live - ref;
        },
        valueFormatter: deltaFmt,
        filter: 'agNumberColumnFilter',
        filterParams: agGridUtils.getNumericFilterParams(),
      },
    ];

    const competitorColumns = tsgFeedCompetitors.map(comp => ({
      headerName: comp.competitorDisplayDescription || comp.competitorDescription,
      groupId: `tsg::${tsgKey}::competitor::${comp.competitorKey}`,
      headerClass: 'border-right',
      marryChildren: true,
      children: [
        {
          headerName: `${tkey('columns.competitorReferenceDatePrice')} (${t(
            'clientCurrencySymbol'
          )})`,
          field: `toolStoreGroups.${tsgKey}.competitor.feed.${
            comp.competitorKey
          }.competitorReferenceDatePrice`,
          colId: `tsg::${tsgKey}::competitor::${comp.competitorKey}::refPrice`,
          width: 130,
          valueFormatter: currencyFmt,
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.getNumericFilterParams(),
        },
        ...(showHistoricalCompetitorPrices
          ? [
              {
                headerName: t('pricing.competitorPriceTwoWeeksAgo'),
                field: `toolStoreGroups.${tsgKey}.competitor.feed.${
                  comp.competitorKey
                }.weekPrices.2`,
                colId: `tsg::${tsgKey}::competitor::${comp.competitorKey}::weekPrice2`,
                width: 110,
                valueFormatter: currencyFmt,
                filter: 'agNumberColumnFilter',
                filterParams: agGridUtils.getNumericFilterParams(),
              },
              {
                headerName: t('pricing.competitorPriceLastWeek'),
                field: `toolStoreGroups.${tsgKey}.competitor.feed.${
                  comp.competitorKey
                }.weekPrices.1`,
                colId: `tsg::${tsgKey}::competitor::${comp.competitorKey}::weekPrice1`,
                width: 110,
                valueFormatter: currencyFmt,
                filter: 'agNumberColumnFilter',
                filterParams: agGridUtils.getNumericFilterParams(),
              },
            ]
          : []),
        {
          headerName: `${tkey('columns.liveCompetitorPrice')} (${t('clientCurrencySymbol')})`,
          field: `toolStoreGroups.${tsgKey}.competitor.feed.${comp.competitorKey}.competitorPrice`,
          colId: `tsg::${tsgKey}::competitor::${comp.competitorKey}::competitorPrice`,
          width: 130,
          valueFormatter: currencyFmt,
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.getNumericFilterParams(),
        },
        {
          headerName: `${tkey('columns.competitorPriceDelta')} (${t('clientCurrencySymbol')})`,
          colId: `tsg::${tsgKey}::competitor::${comp.competitorKey}::delta`,
          width: 110,
          valueGetter: params => {
            const compData = get(
              params,
              `data.toolStoreGroups.${tsgKey}.competitor.feed.${comp.competitorKey}`
            );
            if (!compData) return null;
            const { competitorPrice, competitorReferenceDatePrice } = compData;
            if (isNil(competitorPrice) || isNil(competitorReferenceDatePrice)) return null;
            return competitorPrice - competitorReferenceDatePrice;
          },
          valueFormatter: deltaFmt,
          filter: 'agNumberColumnFilter',
          filterParams: agGridUtils.getNumericFilterParams(),
        },
        {
          headerName: tkey('columns.competitorFeedPromotion'),
          field: `toolStoreGroups.${tsgKey}.competitor.feed.${
            comp.competitorKey
          }.competitorPromotion`,
          colId: `tsg::${tsgKey}::competitor::${comp.competitorKey}::competitorFeedPromotion`,
          width: 150,
          cellDataType: 'text',
          tooltipField: `toolStoreGroups.${tsgKey}.competitor.feed.${
            comp.competitorKey
          }.competitorPromotion`,
        },
        {
          headerName: tkey('columns.ignoreActive'),
          colId: `tsg::${tsgKey}::competitor::${comp.competitorKey}::ignoreActive`,
          width: 90,
          cellClass: params =>
            vm.getEditableCellClasses(params, tsgKey, comp.competitorKey, ['campaign-toggle-cell']),
          editable: params =>
            !!get(
              params,
              `data.toolStoreGroups.${tsgKey}.competitor.feed.${comp.competitorKey}.competitorKey`
            ),
          cellDataType: 'boolean',
          cellEditor: 'agCheckboxCellEditor',
          valueGetter: params => {
            const priceIgnorePath = `toolStoreGroups.${tsgKey}.competitor.feed.${
              comp.competitorKey
            }.priceIgnore`;
            const priceIgnore = get(params.data, priceIgnorePath);
            // Require both dates to consider a campaign active
            return !!(priceIgnore && priceIgnore.effectiveDate && priceIgnore.expiryDate);
          },
          valueSetter: params =>
            vm.handleCampaignToggleValueSetter(params, tsgKey, comp.competitorKey),
          suppressMenu: true,
          sortable: false,
          filter: false,
        },
        {
          headerName: tkey('columns.ignorePeriod'),
          colId: `tsg::${tsgKey}::competitor::${comp.competitorKey}::ignorePeriod`,
          width: 220,
          editable: params =>
            !!get(
              params,
              `data.toolStoreGroups.${tsgKey}.competitor.feed.${comp.competitorKey}.competitorKey`
            ),
          headerClass: 'border-right',
          cellClass: params =>
            vm.getEditableCellClasses(params, tsgKey, comp.competitorKey, ['border-right']),
          cellRenderer: 'ignorePeriodCellRenderer',
          cellRendererParams: { tsgKey, competitorKey: comp.competitorKey },
          cellEditor: 'datePickerEditor',
          cellEditorPopup: true,
          cellEditorPopupPosition: 'under',
          cellEditorParams: params =>
            vm.getCampaignDatePickerParams(params, tsgKey, comp.competitorKey),
          valueGetter: params => {
            const priceIgnore = get(
              params,
              `data.toolStoreGroups.${tsgKey}.competitor.feed.${comp.competitorKey}.priceIgnore`
            );
            if (!priceIgnore || !priceIgnore.effectiveDate || !priceIgnore.expiryDate) return null;
            const start = vm.formatLocalisedDate(priceIgnore.effectiveDate);
            const end = vm.formatLocalisedDate(priceIgnore.expiryDate);
            return `${start} – ${end}`;
          },
          valueSetter: params =>
            vm.handleCampaignDateValueSetter(params, tsgKey, comp.competitorKey),
          filter: 'agTextColumnFilter',
        },
      ],
    }));

    return [...ownPriceColumns, ...competitorColumns];
  };

  const tsgMap = storegroupKeyNameMap || {};

  const tsgColumnGroups = storeGroupChains.map(chain => ({
    headerName: chain.description,
    groupId: `chain::${chain._id}`,
    headerClass: 'border-right',
    marryChildren: true,
    children: Object.entries(tsgMap)
      .filter(([tsgKey]) => chain.toolStoreGroupKeys.includes(tsgKey))
      .map(([tsgKey, tsgDescription]) => ({
        headerName: tsgDescription,
        groupId: `tsg::${tsgKey}`,
        headerClass: 'border-right',
        marryChildren: true,
        children: buildTsgColumns(tsgKey, tsgDescription),
      })),
  }));

  return addHeaderTooltips([...productColumns, ...tsgColumnGroups]);
}
